import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CheckCircle2, LoaderCircle } from "lucide-react";
import { Expeditor, Order } from "../../../../services";
import CustomButton from "../../../custom/custom-button/CustomButton";
import CustomSelect from "../../../custom/custom-select";
import ReassignUsersIcon from "../../../shared/icons/ReassignUsersIcon";
import style from "./OrderStatusTimeline.module.scss";

const ReassignExpeditorCard = ({
  order,
  fetchOrderData,
  onForceUpdate,
  reassignExpeditor = Order.reassignExpeditor,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [partnersError, setPartnersError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const submitLock = useRef(false);
  const mounted = useRef(false);
  const text = (key) => t(`orderInfoClient.timeline.reassignment.${key}`);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;
    let cancelled = false;
    setPartnersLoading(true);
    setPartnersError(false);
    Expeditor.getPartners()
      .then(({ data }) => {
        if (cancelled) return;
        const available = (Array.isArray(data) ? data : [])
          .map((partner) => ({ ...partner, id: partner.expeditorCompanyId || partner.id }))
          .filter(
            (partner) =>
              partner.state === "Confirmed" &&
              partner.id &&
              partner.companyName &&
              String(partner.id) !== String(order.expeditorId),
          );
        setPartners(available);
      })
      .catch(() => {
        if (!cancelled) setPartnersError(true);
      })
      .finally(() => {
        if (!cancelled) setPartnersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, retry, order.expeditorId]);

  const handleConfirm = async () => {
    if (submitLock.current || !selectedPartner || !order.id) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      await reassignExpeditor(order.id, { expeditorId: selectedPartner.id, comment: "" });
    } catch (error) {
      submitLock.current = false;
      if (mounted.current) {
        setSubmitting(false);
        toast.error(text("submitError"));
      }
      return;
    }
    if (mounted.current) {
      setSubmitted(true);
      setSubmitting(false);
      toast.success(text("success"));
    }
    fetchOrderData?.({ silent: true });
    onForceUpdate?.();
  };

  return (
    <div className={style.reassignment}>
      <div className={style.reassignment_heading}>
        <div className={style.reassignment_icon}>
          <ReassignUsersIcon />
        </div>
        <div className={style.reassignment_copy}>
          <h4 className={style.reassignment_title}>{text("title")}</h4>
          <p className={style.reassignment_description}>{text("description")}</p>
        </div>
      </div>
      {submitted ? (
        <div className={style.reassignment_success} role="status">
          <CheckCircle2 size={18} aria-hidden="true" />
          <span>{text("success")}</span>
        </div>
      ) : (
        <>
          {!expanded && (
            <div className={style.reassignment_trigger}>
              <CustomButton
                type="button"
                text={text("title")}
                isNavy
                isSmall
                onClick={() => setExpanded(true)}
                aria-expanded={false}
              />
            </div>
          )}
          {expanded && (
            <div className={style.reassignment_form}>
              {partnersLoading ? (
                <div className={style.reassignment_state} role="status">
                  <LoaderCircle size={18} className={style.reassignment_spinner} aria-hidden="true" />
                  <span>{text("loading")}</span>
                </div>
              ) : partnersError ? (
                <>
                  <p className={style.reassignment_error} role="alert">
                    {text("loadError")}
                  </p>
                  <CustomButton
                    type="button"
                    text={text("retry")}
                    isWhite
                    isSmall
                    onClick={() => setRetry((value) => value + 1)}
                  />
                </>
              ) : partners.length === 0 ? (
                <p className={style.reassignment_state} role="status">
                  {text("empty")}
                </p>
              ) : (
                <CustomSelect
                  options={partners}
                  keyName="companyName"
                  labelText={text("companyLabel")}
                  inputId={`reassign-expeditor-${order.id}`}
                  placeholder={text("placeholder")}
                  defaultValue={selectedPartner}
                  onSubmit={setSelectedPartner}
                  returnObject
                  isDisabled={submitting}
                  bgWhite
                  usePortal
                />
              )}
              <div className={style.reassignment_actions}>
                <CustomButton
                  type="button"
                  text={t("orderInfoClient.timeline.cancel")}
                  isNavy
                  isWhite
                  isSmall
                  disabled={submitting}
                  onClick={() => {
                    setExpanded(false);
                    setSelectedPartner(null);
                  }}
                />
                {!partnersLoading && !partnersError && partners.length > 0 && (
                  <CustomButton
                    type="button"
                    text={t("orderInfoClient.timeline.confirm")}
                    isNavy
                    isSmall
                    onClick={handleConfirm}
                    isLoading={submitting}
                    disabled={!selectedPartner || submitting}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReassignExpeditorCard;
